import { Button, InlineLoading } from '@carbon/react';
import {
  CheckmarkFilled,
  CheckmarkOutline,
  Edit,
  ErrorFilled,
  Information,
  Map as MapIcon,
  WarningFilled,
  WarningHex,
} from '@carbon/react/icons';
import { DataTable, DataTableColumnDef } from 'components/data-table';
import { Link } from 'components/link/link';
import { WorkflowStatusPopover } from 'components/workflow-status-popover/workflow-status-popover';
import { useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import routes from '~/routes';
import {
  OUTCOME_LABELS,
  isRetryCandidate,
  isRetryable,
  isSkipped,
  issueText,
  needsGisShape,
  overallStepStatus,
  stepItems,
} from './batch';
import { focusMapRowAtom } from './atoms';
import styles from './bulk-upload.module.scss';
import { EM_DASH, FIELD_LABELS, TABLE_FIELDS, displayValue } from './fields';
import { batchMapRow, mapBlocker } from './map';
import type { BatchRow, RowValues } from './types';

/** A row with the values the table shows: its pending edit, else its payload. */
type ResultRow = BatchRow & { values: RowValues };

const OutcomeIcon = ({ row }: { row: BatchRow }) => {
  switch (row.outcome) {
    case 'success':
      return <CheckmarkFilled className={styles.outcomeSuccess} />;
    case 'success_with_issues':
      return <WarningFilled className={styles.outcomeIssues} />;
    case 'failed':
      return <ErrorFilled className={styles.outcomeFailed} />;
    default:
      return null;
  }
};

/**
 * Running or stalled: the row shows a spinner rather than a result icon.
 * @param {BatchRow} row - A batch row
 * @return {boolean} Whether it shows a spinner
 */
const isRunning = (row: BatchRow) =>
  row.outcome === 'pending' ||
  row.outcome === 'creating' ||
  row.outcome === 'stalled';

/**
 * A running row's one spinner, right of its label: alone in a slot the size of
 * the details button, which takes its place once the row has steps, so it
 * never moves. The upload page's Creating cell uses it too.
 * @return {JSX.Element} The spinner in its slot
 */
export const CreatingSpinner = () => (
  <span className={styles.outcomePendingSlot}>
    <InlineLoading className={styles.outcomeRunning} />
  </span>
);

const ValidityCell = ({ row }: { row: BatchRow }) => {
  if (isSkipped(row)) {
    return (
      <span className={`${styles.statusInvalid} ${styles.statusStatic}`}>
        <WarningHex />
        Invalid
      </span>
    );
  }
  return (
    <span className={styles.statusValid}>
      <CheckmarkOutline />
      Valid
    </span>
  );
};

const OutcomeCell = ({ row }: { row: BatchRow }) => {
  if (isSkipped(row)) return EM_DASH;
  const issue = issueText(row);
  const items = stepItems(row);
  return (
    <div className={styles.outcome}>
      <div className={styles.outcomeLine}>
        {!isRunning(row) && <OutcomeIcon row={row} />}
        <span>{OUTCOME_LABELS[row.outcome]}</span>
        {isRunning(row) && items.length === 0 && <CreatingSpinner />}
        {items.length > 0 && (
          // Opening the details is not a click on the row.
          <span onClick={(event) => event.stopPropagation()}>
            <WorkflowStatusPopover
              statusItems={items}
              overallStatus={overallStepStatus(row)}
              ariaLabel="View creation details"
              iconDescription="View creation details"
              size="sm"
              // While the row runs the button is a spinner that opens the live
              // steps; once it has a result, the mock's information icon rather
              // than a second copy of the row's status icon.
              triggerIcon={isRunning(row) ? undefined : <Information />}
              // The table runs to the bottom of the screen, and 14 steps do not fit below a
              // lower row.
              fitViewportHeight
              // Rows that wrap long step names and keep the status word and its
              // reason inside the panel.
              showStatusIcon
            />
          </span>
        )}
      </div>
      {issue && (
        <span className={styles.outcomeDetail} title={issue}>
          {issue.split('\n')[0]}
        </span>
      )}
      {needsGisShape(row) && (
        <span className={styles.outcomeDetail}>Needs GIS Shape</span>
      )}
    </div>
  );
};

/**
 * Why the row's Edit before retry is off, or null when it is on.
 * @param {BatchRow} row - A retry candidate
 * @param {string | null} retryBlocker - Why this user cannot retry at all
 * @return {string | null} The reason
 */
const editBlocker = (row: BatchRow, retryBlocker: string | null) => {
  if (row.asset) return 'The asset exists, so its values can no longer change';
  if (!isRetryable(row)) return 'Still running; edit it once it finishes';
  return retryBlocker;
};

interface ResultsTableProps {
  rows: BatchRow[];
  /** Edited values waiting for the retry, by row id. */
  edits: Record<number, RowValues>;
  /** Why the rows cannot be edited or retried now; null when they can. */
  retryBlocker: string | null;
  onEdit: (row: BatchRow) => void;
}

/**
 * Every row with its validity and creation status (a skipped row: Invalid,
 * an em dash, nothing to open or retry), the step and reason behind
 * an issue, the created asset, and View creation details. A Failed row whose
 * asset was never created can be edited before it is retried.
 * @param {ResultsTableProps} props - Rows, pending edits, and Edit
 * @return {JSX.Element} The table
 */
export const ResultsTable = ({
  rows,
  edits,
  retryBlocker,
  onEdit,
}: ResultsTableProps) => {
  const focusMapRow = useSetAtom(focusMapRowAtom);
  const data = useMemo(
    () =>
      rows.map((row): ResultRow => ({
        ...row,
        values: edits[row.id] ?? row.payload,
      })),
    [rows, edits]
  );
  const columns = useMemo<DataTableColumnDef<ResultRow>[]>(
    () => [
      {
        id: 'validity',
        header: 'Validity',
        size: 120,
        accessorFn: (row) => (isSkipped(row) ? 'Invalid' : 'Valid'),
        cell: ({ row }) => <ValidityCell row={row.original} />,
      },
      {
        id: 'creationStatus',
        header: 'Creation status',
        size: 260,
        cell: ({ row }) => <OutcomeCell row={row.original} />,
        meta: { overflowVisible: true },
      },
      {
        id: 'asset',
        header: 'Asset',
        size: 160,
        accessorFn: (row) => row.asset?.name ?? row.asset?.id ?? EM_DASH,
        cell: ({ row }) => {
          const { asset } = row.original;
          if (!asset) return EM_DASH;
          return (
            <Link
              to={generatePath(routes.crmTractWizardEditPath, {
                tractId: asset.id,
              })}
            >
              {asset.name ?? asset.id}
            </Link>
          );
        },
      },
      ...TABLE_FIELDS.map((key): DataTableColumnDef<ResultRow> => ({
        id: key,
        header: FIELD_LABELS[key],
        size: key === 'tractFullLegalDescription' ? 280 : 150,
        accessorFn: ({ values, accountId }) => {
          if (key !== 'crmAccount') return displayValue(values[key]);
          return displayValue(values.crmAccount ?? accountId);
        },
      })),
      {
        id: 'actions',
        header: 'Actions',
        size: 104,
        meta: { overflowVisible: true },
        cell: ({ row }) => {
          const mapBlocked = mapBlocker(
            batchMapRow(row.original, row.original.values)
          );
          const blocker = editBlocker(row.original, retryBlocker);
          return (
            // Its buttons act on their own; only the rest of the row opens the map.
            <div
              className={styles.rowActions}
              onClick={(event) => event.stopPropagation()}
            >
              {isRetryCandidate(row.original) && (
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={Edit}
                  iconDescription={blocker ?? 'Edit asset before retrying'}
                  tooltipPosition="left"
                  disabled={!!blocker}
                  onClick={() => onEdit(row.original)}
                />
              )}
              {/* A skipped row cannot be edited here, so nothing could unblock its map. */}
              {!isSkipped(row.original) && (
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={MapIcon}
                  iconDescription={mapBlocked ?? 'View asset on map'}
                  tooltipPosition="left"
                  disabled={!!mapBlocked}
                  onClick={() => focusMapRow(String(row.original.id))}
                />
              )}
            </div>
          );
        },
      },
    ],
    [retryBlocker, onEdit, focusMapRow]
  );

  return (
    <div className={styles.tableWrapper}>
      <DataTable<ResultRow>
        data={data}
        columns={columns}
        getRowId={(row) => String(row.id)}
        onSelectionChange={({ selection }) => {
          const row = data.find(
            (candidate) => String(candidate.id) === selection.toggledNodes[0]
          );
          if (
            row &&
            !isSkipped(row) &&
            !mapBlocker(batchMapRow(row, row.values))
          ) {
            focusMapRow(String(row.id));
          }
        }}
        initialTableState={{
          columnPinning: {
            left: ['validity', 'creationStatus'],
            right: ['actions'],
          },
        }}
        config={{
          globalSearch: true,
          columnFilters: false,
          sorting: false,
          columnReordering: false,
          columnVisibility: false,
        }}
        searchPlaceholder="Search assets"
      />
    </div>
  );
};
